import { useEffect, useState } from "react";
import { ApiError, api, formatMoney } from "./api";
import { Alert, Modal, Working } from "./ui";

/**
 * Transfers out that OCBC's alerts reported, waiting for someone to say what they were for
 * (Daren, 14 October 2026: the bank allows about 14 characters, and the details were being
 * forgotten). Shown wherever people work, desk and till, until each is described and done.
 */
export interface OutgoingTransfer {
  readonly id: string;
  readonly amountCents: number;
  readonly paidAt: string;
  readonly description: string | null;
  readonly describedBy: string | null;
  readonly settled: boolean;
}

/** How many are waiting, fetched again every few minutes and whenever asked. */
export function usePendingTransfers(enabled: boolean): { transfers: OutgoingTransfer[]; reload: () => void } {
  const [transfers, setTransfers] = useState<OutgoingTransfer[]>([]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = (): void => {
      void api
        .outgoingTransfers()
        .then((result) => live && setTransfers(result.transfers))
        .catch(() => undefined);
    };
    load();
    const timer = window.setInterval(load, 3 * 60_000);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [enabled, tick]);
  return { transfers, reload: () => setTick((at) => at + 1) };
}

const when = (at: string): string =>
  new Date(at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Singapore" });

/** The waiting transfers, each with a box to say what it was for. */
export function OutgoingTransfers({
  transfers,
  onClose,
  onChanged,
}: {
  readonly transfers: readonly OutgoingTransfer[];
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): React.ReactElement {
  const [words, setWords] = useState<Record<string, string>>(() => Object.fromEntries(transfers.map((one) => [one.id, one.description ?? ""])));
  const [busy, setBusy] = useState<string | undefined>();
  const [problem, setProblem] = useState<string | undefined>();
  const [done, setDone] = useState<string[]>([]);

  async function save(id: string, settled: boolean): Promise<void> {
    setBusy(id);
    setProblem(undefined);
    try {
      await api.describeOutgoing(id, words[id] ?? "", settled);
      if (settled) setDone((was) => [...was, id]);
      onChanged();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not save it");
    } finally {
      setBusy(undefined);
    }
  }

  const left = transfers.filter((one) => !done.includes(one.id));
  return (
    <Modal
      open
      size="lg"
      title="Transfers out to explain"
      onClose={onClose}
      footer={
        <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
          Close
        </button>
      }
    >
      {problem ? <Alert tone="danger">{problem}</Alert> : null}
      <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50">
        Money that left the OCBC account, from the bank's alert emails. The bank keeps only a few words, so write down what each
        was for, in full, while it is fresh: who was paid, what for, and any buyback or invoice number. Then mark it done.
      </p>
      {left.length === 0 ? (
        <p className="mb-0 text-success font-semibold">Every transfer out is explained.</p>
      ) : (
        left.map((transfer) => (
          <div key={transfer.id} className="border border-defaultborder rounded-md p-3 mb-3">
            <div className="flex justify-between gap-3 mb-2">
              <span className="font-semibold text-[1.0625rem]">-{formatMoney(transfer.amountCents)}</span>
              <span className="text-[0.813rem] text-[#8c9097] dark:text-white/50">{when(transfer.paidAt)}</span>
            </div>
            <textarea
              rows={2}
              className="form-control"
              aria-label={`What the ${formatMoney(transfer.amountCents)} transfer was for`}
              placeholder="What it was for, e.g. Buyback B26A11A03, Zapdos, PayNow to the seller"
              value={words[transfer.id] ?? ""}
              onChange={(event) => setWords((was) => ({ ...was, [transfer.id]: event.target.value }))}
            />
            <div className="flex flex-wrap justify-end gap-2 mt-2">
              <button type="button" className="ti-btn ti-btn-light !py-1 !px-3 !text-[0.75rem] !mb-0" disabled={busy === transfer.id} onClick={() => void save(transfer.id, false)}>
                Save for now
              </button>
              <button
                type="button"
                className="ti-btn ti-btn-success-full !py-1 !px-3 !text-[0.75rem] !mb-0"
                disabled={busy === transfer.id || !(words[transfer.id] ?? "").trim()}
                onClick={() => void save(transfer.id, true)}
              >
                {busy === transfer.id ? <Working label="Saving" /> : "Save and mark done"}
              </button>
            </div>
          </div>
        ))
      )}
    </Modal>
  );
}
